import React, { useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { faker } from "@faker-js/faker";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
} from "@heroui/react";
import { useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-auth";

import { useChatHistoryStore } from "@/stores/ChatHistoryStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { PageTitle } from "@/components/UI/PageTitle";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

// Generate last 30 days labels
const labels = Array.from({ length: 30 }, (_, i) => {
  const date = new Date();

  date.setDate(date.getDate() - (29 - i));

  return date.toLocaleDateString("en-GB", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
});

// Chart configurations and data
export const options = {
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 2,
  plugins: {
    legend: {
      position: "top" as const,
    },
    title: {
      display: true,
      text: "Daily Usage Metrics",
    },
  },
  scales: {
    x: {
      stacked: true,
    },
    y: {
      stacked: true,
      type: "linear" as const,
      display: true,
      position: "left" as const,
      title: {
        display: true,
        text: "Number of Invocations",
      },
    },
  },
};

export const data = {
  labels,
  datasets: [
    {
      label: "Chat Workflows",
      data: labels.map(() => faker.number.int({ min: 20, max: 80 })),
      backgroundColor: "rgba(53, 162, 235, 0.5)",
      yAxisID: "y",
    },
    {
      label: "Data Processing",
      data: labels.map(() => faker.number.int({ min: 10, max: 40 })),
      backgroundColor: "rgba(75, 192, 192, 0.5)",
      yAxisID: "y",
    },
    {
      label: "Automation Tasks",
      data: labels.map(() => faker.number.int({ min: 5, max: 30 })),
      backgroundColor: "rgba(255, 159, 64, 0.5)",
      yAxisID: "y",
    },
  ],
};

export const performanceData = {
  labels,
  datasets: [
    {
      label: "Daily Cost ($)",
      data: labels.map(() => faker.number.float({ min: 5, max: 50, fractionDigits: 1 })),
      borderColor: "rgb(255, 99, 132)",
      backgroundColor: "rgba(255, 99, 132, 0.5)",
      yAxisID: "y1",
    },
    {
      label: "Data Stored (GB)",
      data: labels.map((_, index) => {
        const baseValue = 10;
        const dailyIncrease = Array.from({ length: index + 1 }, () =>
          faker.number.float({ min: 1, max: 5, fractionDigits: 1 }),
        ).reduce((sum, val) => sum + val, 0);

        return baseValue + dailyIncrease;
      }),
      borderColor: "rgb(153, 102, 255)",
      backgroundColor: "rgba(153, 102, 255, 0.5)",
      yAxisID: "y2",
    },
  ],
};

export const performanceOptions = {
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 2,
  plugins: {
    legend: {
      position: "top" as const,
    },
    title: {
      display: true,
      text: "Resource Metrics",
    },
  },
  scales: {
    y: {
      type: "linear" as const,
      display: true,
      position: "left" as const,
      title: {
        display: true,
        text: "Daily Cost ($)",
      },
    },
    y1: {
      type: "linear" as const,
      display: true,
      position: "right" as const,
      title: {
        display: true,
        text: "Data Stored (GB)",
      },
      grid: {
        drawOnChartArea: false,
      },
    },
    y2: {
      type: "linear" as const,
      display: true,
      position: "right" as const,
      title: {
        display: true,
        text: "Storage (GB)",
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
};

export const satisfactionData = {
  labels,
  datasets: [
    {
      label: "Satisfaction Score",
      data: labels.map(() => faker.number.float({ min: 3.5, max: 5, fractionDigits: 1 })),
      borderColor: "rgb(75, 192, 192)",
      backgroundColor: "rgba(75, 192, 192, 0.5)",
      yAxisID: "y",
    },
    {
      label: "Positive/Negative Ratio",
      data: labels.map(() => faker.number.float({ min: 2, max: 8, fractionDigits: 1 })),
      borderColor: "rgb(255, 159, 64)",
      backgroundColor: "rgba(255, 159, 64, 0.5)",
      yAxisID: "y1",
    },
  ],
};

export const satisfactionOptions = {
  responsive: true,
  maintainAspectRatio: true,
  aspectRatio: 2,
  plugins: {
    legend: {
      position: "top" as const,
    },
    title: {
      display: true,
      text: "Customer Satisfaction Metrics",
    },
  },
  scales: {
    y: {
      type: "linear" as const,
      display: true,
      position: "left" as const,
      title: {
        display: true,
        text: "Satisfaction Score (1-5)",
      },
      min: 1,
      max: 5,
    },
    y1: {
      type: "linear" as const,
      display: true,
      position: "right" as const,
      title: {
        display: true,
        text: "Positive/Negative Ratio",
      },
      grid: {
        drawOnChartArea: false,
      },
    },
  },
};

export const MetricsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { chatHistory, fetchChatHistory } = useChatHistoryStore();
  const { adminClient } = useWebuildAPIClient();
  const { user } = useAuth();
  const { brand } = useBrand();

  useEffect(() => {
    if (adminClient && user?.id) {
      fetchChatHistory(user.id, adminClient);
    }
  }, [adminClient, user, fetchChatHistory]);

  const handleRowClick = (workflowId: string, runId: string) => {
    navigate(`/workflows/${workflowId}/${runId}`);
  };

  // Calculate metrics from actual chat history data
  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
        return "success";
      case "failed":
        return "danger";
      case "streaming":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "success":
        return t("Completed");
      case "failed":
        return t("Failed");
      case "streaming":
        return t("In Progress");
      default:
        return t("Unknown");
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);

    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs}s`;
    }

    return `${diffSecs}s`;
  };

  const calculateTotalCost = (metrics: any): string => {
    if (!metrics?.usage) return "$0.00";

    // Sum up costs from all models
    let totalCost = 0;

    Object.values(metrics.usage).forEach((modelUsage: any) => {
      if (modelUsage?.cost) {
        // Convert string cost to number
        const cost = parseFloat(modelUsage.cost);

        if (!isNaN(cost)) {
          totalCost += cost;
        }
      }
    });

    return `$${totalCost.toFixed(2)}`;
  };

  return (
    <div className="p-4">
      <PageTitle
        centered
        dataId="metrics-title"
        icon="solar:pie-chart-2-outline"
        img={`/brands/${brand.prefix || "webuild"}/solutions.svg`}
        subtitle={t("ngv.monitoring.subtitle")}
        title={t("ngv.monitoring.title")}
      />
      <div className="grid grid-cols-3 ">
        <div className="rounded-lg shadow p-4">
          <div className="">
            <Bar data={data} options={options} />
          </div>
        </div>
        <div className="rounded-lg shadow p-4">
          <div className="h-[400px]">
            <Line data={performanceData} options={performanceOptions} />
          </div>
        </div>
        <div className="rounded-lg shadow p-4">
          <div className="h-[400px]">
            <Line data={satisfactionData} options={satisfactionOptions} />
          </div>
        </div>
      </div>

      <div className="">
        <div className="rounded-lg shadow p-4">
          <Table aria-label={t("Workflow metrics table")}>
            <TableHeader>
              <TableColumn>{t("Workflow ID")}</TableColumn>
              <TableColumn>{t("Chat ID")}</TableColumn>
              <TableColumn>{t("User")}</TableColumn>
              <TableColumn>{t("Status")}</TableColumn>
              <TableColumn>{t("Started")}</TableColumn>
              <TableColumn>{t("Duration")}</TableColumn>
              <TableColumn>{t("Cost")}</TableColumn>
            </TableHeader>
            <TableBody>
              {chatHistory.map((chat) => {
                const status = chat.status || "unknown";
                const startTime = chat.createdAt;
                const endTime = chat.updatedAt;

                return (
                  <TableRow
                    key={chat.id}
                    className="cursor-pointer hover:bg-gray-100"
                    onClick={() => handleRowClick("chat", chat.id)}
                  >
                    <TableCell>{t("Chat")}</TableCell>
                    <TableCell>{chat.id}</TableCell>
                    <TableCell>{chat.username}</TableCell>
                    <TableCell>
                      <Chip color={getStatusColor(status)} size="sm" variant="flat">
                        {getStatusText(status)}
                      </Chip>
                    </TableCell>
                    <TableCell>{new Date(startTime).toLocaleString()}</TableCell>
                    <TableCell>{formatDuration(startTime, endTime)}</TableCell>
                    <TableCell>{calculateTotalCost(chat.metrics)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
